// The Authority Gap — Legal & Policy content.
// Structured as plain data so a single LegalPage renderer handles all of them.
// Reviewed by counsel before legal reliance.

export const COMPANY = "The Authority Gap";
export const DOMAIN = "theauthoritygap.co";
export const SUPPORT_EMAIL = "support@theauthoritygap.co";
export const EFFECTIVE_DATE = "January 1, 2026";
export const GOVERNING_LAW = "[Insert State / Country]"; // filled by counsel
export const BUSINESS_ADDRESS = "[Insert Business Address]"; // filled by counsel

// Helper — shared closing paragraph used by most policies
const CONTACT_BLOCK = (subject) =>
  `Questions about this policy may be directed to ${SUPPORT_EMAIL} with the subject line "${subject}".`;

export const POLICIES = {
  // ============================================================
  terms: {
    slug: "terms",
    title: "Terms of Service",
    eyebrow: "Legal",
    intro:
      `These Terms of Service ("Terms") govern your access to and use of the websites, software, frameworks, ` +
      `templates, AI assistants, assessments, scorecards, audits, and other educational materials provided by ` +
      `${COMPANY} ("we," "us," or "our"), including the AuthorityOS ecosystem. By accessing or using any of ` +
      `our services you agree to be bound by these Terms.`,
    sections: [
      { h: "1. Educational Use Only", p: [
        `All materials provided by ${COMPANY} are educational and informational only. Nothing we publish, ` +
        `generate, distribute, license, or transmit constitutes legal advice, medical advice, mental health ` +
        `advice, therapy, tax advice, financial advice, investment advice, insurance advice, regulatory ` +
        `advice, compliance certification, accreditation, professional consultation, government approval, ` +
        `or professional representation.`,
        `Use of our services does not create an attorney-client, doctor-patient, fiduciary, advisor-client, ` +
        `or any other professional relationship between you and ${COMPANY}.`,
      ]},
      { h: "2. No Professional Relationship", p: [
        `${COMPANY} is not a law firm, medical practice, therapy practice, accounting firm, engineering firm, ` +
        `financial advisory firm, certifying body, accreditation body, or regulator. Our team members are ` +
        `not acting as your lawyer, doctor, therapist, accountant, engineer, financial advisor, tax advisor, ` +
        `or compliance officer when providing any service.`,
      ]},
      { h: "3. User Responsibility", p: [
        `You are solely responsible for evaluating the suitability of any information, framework, or output ` +
        `for your specific circumstances and for the decisions you make based upon it. You should consult ` +
        `appropriately qualified professionals before acting on any material we provide.`,
      ]},
      { h: "4. Accounts & Access", p: [
        `Some services require an account. You agree to provide accurate information, keep your credentials ` +
        `confidential, and notify us promptly of any unauthorized use. You are responsible for all activity ` +
        `under your account.`,
      ]},
      { h: "5. Subscriptions, Digital Products & Refunds", p: [
        `Subscriptions renew automatically until cancelled in your account or by contacting ${SUPPORT_EMAIL}. ` +
        `Digital products and one-time services are delivered electronically. See our Refund Policy for the ` +
        `circumstances in which refunds may be available.`,
      ]},
      { h: "6. Content You Provide", p: [
        `You retain ownership of content you upload, submit, or generate through our services. You grant ` +
        `${COMPANY} a limited, worldwide, non-exclusive license to host, process, transmit, and display that ` +
        `content solely as necessary to operate the service for you, including processing through third-party ` +
        `AI providers. You represent that you have all rights necessary to grant this license.`,
      ]},
      { h: "7. Our Content & Intellectual Property", p: [
        `All frameworks, templates, AI assistant outputs (where attributable to us), software, written ` +
        `materials, designs, logos, and trademarks of ${COMPANY} and the AuthorityOS ecosystem are owned by ` +
        `${COMPANY} or our licensors and are protected by intellectual property law. See our Intellectual ` +
        `Property Policy for permitted uses.`,
      ]},
      { h: "8. Acceptable Use", p: [
        `You agree not to misuse the services. See our Acceptable Use Policy for prohibited activities. ` +
        `We may suspend or terminate accounts that violate these Terms.`,
      ]},
      { h: "9. AI Output", p: [
        `Some outputs are generated with the assistance of artificial intelligence. AI systems may produce ` +
        `inaccurate, incomplete, outdated, or incorrect information. You are responsible for independently ` +
        `evaluating AI output. See our AI Use Policy and AI Disclosure Policy.`,
      ]},
      { h: "10. No Guarantees", p: [
        `${COMPANY} does not guarantee any outcome, ranking, approval, certification, compliance status, ` +
        `revenue, traffic, lead volume, business growth, legal protection, or other result. Statements ` +
        `regarding capability are phrased as "may help," "may support," or "may improve" rather than as ` +
        `promises of result.`,
      ]},
      { h: "11. Disclaimer of Warranties", p: [
        `THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER ` +
        `EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ` +
        `ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ` +
        `ERROR-FREE, OR SECURE.`,
      ]},
      { h: "12. Limitation of Liability", p: [
        `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${COMPANY.toUpperCase()} AND ITS AFFILIATES, OFFICERS, ` +
        `EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, ` +
        `OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, OR GOODWILL. OUR AGGREGATE LIABILITY FOR ` +
        `ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICES WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT ` +
        `YOU PAID US IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) $100 USD.`,
      ]},
      { h: "13. Indemnification", p: [
        `You agree to indemnify and hold harmless ${COMPANY} and its affiliates from any claims, damages, ` +
        `liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of your use ` +
        `of the services, your content, your breach of these Terms, or your violation of any law or right ` +
        `of a third party.`,
      ]},
      { h: "14. Dispute Resolution & Arbitration", p: [
        `Any dispute arising out of or relating to these Terms or the services will be resolved exclusively ` +
        `through binding individual arbitration administered under the rules of a recognized arbitration ` +
        `body in ${GOVERNING_LAW}, except that either party may bring an individual action in small claims ` +
        `court. YOU AND ${COMPANY.toUpperCase()} WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION OR ` +
        `CLASS-WIDE ARBITRATION.`,
      ]},
      { h: "15. Governing Law", p: [
        `These Terms are governed by the laws of ${GOVERNING_LAW}, without regard to conflict-of-laws rules. ` +
        `If arbitration is unavailable, exclusive jurisdiction and venue lie in the courts located in ${GOVERNING_LAW}.`,
      ]},
      { h: "16. Termination", p: [
        `We may suspend or terminate your access at any time for any reason, including violation of these ` +
        `Terms. Provisions intended to survive termination — including ownership, disclaimers, limitations ` +
        `of liability, indemnification, and dispute resolution — will survive.`,
      ]},
      { h: "17. Changes to These Terms", p: [
        `We may update these Terms from time to time. Material changes will be communicated by posting the ` +
        `updated Terms with a new effective date. Continued use of the services after the update constitutes ` +
        `acceptance.`,
      ]},
      { h: "18. Contact", p: [CONTACT_BLOCK("Terms of Service")] },
    ],
  },

  // ============================================================
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Legal",
    intro:
      `This Privacy Policy describes how ${COMPANY} collects, uses, discloses, and protects information ` +
      `when you use our websites and services across the AuthorityOS ecosystem.`,
    sections: [
      { h: "1. Information We Collect", p: [
        `Account information: name, email, password hash, profile data.`,
        `User-generated content: documents, notes, prompts, assessments, conversations with AI assistants.`,
        `Uploaded documents and files you submit for processing.`,
        `AI conversations and outputs, including transcripts and metadata.`,
        `Analytics data: device, browser, IP address, pages visited, referrer, session duration.`,
        `Payment information: handled by our payment processors (we do not store full card numbers).`,
        `Cookies and similar technologies. See our Cookie Policy.`,
        `Email subscriptions and marketing preferences.`,
      ]},
      { h: "2. How We Use Information", p: [
        `To operate, maintain, secure, and improve the services; to deliver AI-generated outputs you request; ` +
        `to process payments; to send transactional and (with consent) marketing communications; to comply ` +
        `with legal obligations; and to enforce our Terms.`,
      ]},
      { h: "3. AI Processing & Third-Party Providers", p: [
        `Uploaded content, prompts, and conversation data may be transmitted to and processed by third-party ` +
        `AI providers (for example, large language model providers) solely to deliver the requested output. ` +
        `We select providers with documented privacy practices, but we make no guarantee of confidentiality ` +
        `beyond those published practices. You should not upload highly sensitive information (such as ` +
        `regulated health, legal, or financial records) unless you have determined that the provider's ` +
        `terms and security posture are acceptable for that purpose.`,
      ]},
      { h: "4. Content Ownership", p: [
        `You retain ownership of all content you upload or generate. We receive only the limited rights ` +
        `necessary to operate the service for you, as described in our Terms of Service.`,
      ]},
      { h: "5. Sharing", p: [
        `We do not sell personal information. We share information with service providers (hosting, ` +
        `analytics, email, payment, AI providers) under contracts that restrict their use of the data; ` +
        `with successors in a corporate transaction; and when required by law or to protect rights, safety, ` +
        `or property.`,
      ]},
      { h: "6. Cookies & Analytics", p: [
        `We use cookies and similar technologies for authentication, preferences, and analytics. See our ` +
        `Cookie Policy for details and choices.`,
      ]},
      { h: "7. Security", p: [
        `We use commercially reasonable administrative, technical, and physical safeguards. No method of ` +
        `transmission or storage is perfectly secure, and we cannot guarantee absolute security.`,
      ]},
      { h: "8. Data Retention", p: [
        `We retain information for as long as necessary to provide the services and for legitimate business ` +
        `or legal purposes. You may request deletion as described below.`,
      ]},
      { h: "9. Your Rights", p: [
        `Depending on your jurisdiction, you may have rights to access, correct, delete, port, or restrict ` +
        `processing of your personal information, and to object or withdraw consent. To exercise these ` +
        `rights, contact ${SUPPORT_EMAIL}.`,
      ]},
      { h: "10. Children", p: [
        `The services are not directed to children under 13 (or the minimum age in your jurisdiction). We ` +
        `do not knowingly collect personal information from children.`,
      ]},
      { h: "11. International Transfers", p: [
        `Information may be processed in countries other than your own. Where applicable, we use lawful ` +
        `transfer mechanisms.`,
      ]},
      { h: "12. Changes", p: [
        `We may update this Privacy Policy. Material changes will be posted with a new effective date.`,
      ]},
      { h: "13. Contact", p: [CONTACT_BLOCK("Privacy Policy")] },
    ],
  },

  // ============================================================
  "ai-use": {
    slug: "ai-use",
    title: "AI Use Policy",
    eyebrow: "AI Governance",
    intro:
      `This AI Use Policy governs how artificial intelligence is used within the ${COMPANY} and AuthorityOS ecosystem.`,
    sections: [
      { h: "1. Educational & Informational Use Only", p: [
        `All AI-generated outputs are educational and informational only. They are not legal, medical, ` +
        `mental health, tax, financial, investment, insurance, regulatory, or other professional advice.`,
      ]},
      { h: "2. No Professional Relationship", p: [
        `Interacting with any AI assistant in the ecosystem does not create a professional, fiduciary, ` +
        `attorney-client, doctor-patient, advisor-client, or therapist-client relationship.`,
      ]},
      { h: "3. Possible Inaccuracy", p: [
        `AI systems may produce inaccurate, incomplete, outdated, biased, or incorrect information. Outputs ` +
        `should be independently verified before being relied upon for any decision.`,
      ]},
      { h: "4. User Responsibility", p: [
        `You are responsible for independently evaluating all AI output and for consulting appropriately ` +
        `qualified professionals regarding your specific circumstances.`,
      ]},
      { h: "5. No Guaranteed Outcomes", p: [
        `${COMPANY} makes no guarantee regarding the accuracy, completeness, legality, suitability, or ` +
        `fitness for any particular purpose of AI-generated content.`,
      ]},
      { h: "6. Data Sent to AI Providers", p: [
        `Prompts, documents, and conversation context may be transmitted to third-party AI providers to ` +
        `generate responses. Do not submit highly sensitive information unless you have independently ` +
        `assessed the provider's practices.`,
      ]},
      { h: "7. Prohibited Uses", p: [
        `You may not use AI assistants to generate unlawful content; to impersonate professionals you are ` +
        `not licensed as; to provide professional advice to third parties as if it came from ${COMPANY}; ` +
        `or to violate the Acceptable Use Policy.`,
      ]},
      { h: "8. Disclosure of AI Use", p: [
        `When content is generated with material AI assistance and shared with third parties, you are ` +
        `encouraged — and in some jurisdictions required — to disclose that AI was used.`,
      ]},
      { h: "9. Contact", p: [CONTACT_BLOCK("AI Use Policy")] },
    ],
  },

  // ============================================================
  "ai-disclosure": {
    slug: "ai-disclosure",
    title: "AI Disclosure Policy",
    eyebrow: "AI Governance",
    intro:
      `${COMPANY} discloses the use of artificial intelligence throughout the AuthorityOS ecosystem.`,
    sections: [
      { h: "1. Standard AI Disclosure", p: [
        `The following disclosure applies to every AI-generated output across the ecosystem:`,
        `"This response was generated with the assistance of artificial intelligence and is intended ` +
        `solely for educational and informational purposes. Artificial intelligence systems may produce ` +
        `inaccurate, incomplete, outdated, or incorrect information. Users are responsible for ` +
        `independently evaluating all information and consulting qualified professionals before making ` +
        `decisions based upon any information provided. ${COMPANY} makes no guarantee regarding accuracy, ` +
        `completeness, legality, suitability, or fitness for any specific purpose."`,
      ]},
      { h: "2. AI Assistant Disclosure", p: [
        `Every AI assistant in the ecosystem displays the following at the start of each session or in a ` +
        `clearly visible disclosure panel:`,
        `"The information provided by this AI assistant is educational and informational only. This AI ` +
        `assistant is not a lawyer, doctor, therapist, accountant, engineer, financial advisor, tax ` +
        `advisor, regulator, auditor, compliance officer, or licensed professional. Do not rely on this ` +
        `information as a substitute for professional advice. Always consult appropriately qualified ` +
        `professionals regarding your specific circumstances."`,
      ]},
      { h: "3. Where Disclosures Appear", p: [
        `AI disclosures appear in product interfaces, AI assistant responses (or persistent UI), public ` +
        `outputs we publish, and reports we generate where AI played a material role.`,
      ]},
      { h: "4. Contact", p: [CONTACT_BLOCK("AI Disclosure Policy")] },
    ],
  },

  // ============================================================
  "earnings-disclaimer": {
    slug: "earnings-disclaimer",
    title: "Earnings Disclaimer",
    eyebrow: "Disclosure",
    intro:
      `This Earnings Disclaimer applies to all business, SaaS, content, consulting, frameworks, ` +
      `assessments, audits, and AuthorityOS materials provided by ${COMPANY}.`,
    sections: [
      { h: "1. No Income Guarantees", p: [
        `${COMPANY} does not guarantee any specific income, revenue, profit, or business outcome. ` +
        `Statements describing what others have achieved are not representations of what you will achieve.`,
      ]},
      { h: "2. No Business Success Guarantees", p: [
        `Building authority, growing an audience, generating leads, and converting clients depends on many ` +
        `factors outside our control — including your effort, skills, market, offer, timing, and ` +
        `circumstances. We make no guarantee of business success.`,
      ]},
      { h: "3. No Client-Acquisition Guarantees", p: [
        `We do not guarantee that following our frameworks, taking our assessments, or implementing our ` +
        `recommendations will produce any specific number of clients or customers.`,
      ]},
      { h: "4. No SEO, Ranking, or Traffic Guarantees", p: [
        `Search engine rankings, organic traffic, social reach, and discoverability depend on factors ` +
        `controlled by third parties (such as search engines and platforms). ${COMPANY} does not guarantee ` +
        `any ranking, placement, traffic level, or visibility outcome.`,
      ]},
      { h: "5. No Lead-Generation Guarantees", p: [
        `Lead volume, lead quality, conversion rate, and email-list growth vary significantly. No specific ` +
        `result is promised.`,
      ]},
      { h: "6. Results Vary", p: [
        `Individual results will vary. Any example, case study, testimonial, or aggregate figure represents ` +
        `the experience of the specific entity described and is not a guarantee that you will replicate it.`,
      ]},
      { h: "7. Your Responsibility", p: [
        `You are solely responsible for your business decisions, implementation, and outcomes. You should ` +
        `consult qualified business, legal, tax, and financial advisors regarding your specific situation.`,
      ]},
      { h: "8. Contact", p: [CONTACT_BLOCK("Earnings Disclaimer")] },
    ],
  },

  // ============================================================
  "educational-disclaimer": {
    slug: "educational-disclaimer",
    title: "Educational Use Disclaimer",
    eyebrow: "Disclosure",
    intro:
      `All materials provided by ${COMPANY} — including frameworks, templates, assessments, scorecards, ` +
      `audits, AI assistants, and content across the AuthorityOS ecosystem — are educational and ` +
      `informational only.`,
    sections: [
      { h: "1. Not Professional Advice", p: [
        `Nothing we provide constitutes legal, medical, mental health, tax, financial, investment, ` +
        `insurance, regulatory, accreditation, engineering, accounting, or other professional advice.`,
      ]},
      { h: "2. Not a Substitute for Professionals", p: [
        `Our materials are not a substitute for advice from a licensed attorney, physician, therapist, ` +
        `accountant, engineer, financial advisor, tax advisor, or other qualified professional.`,
      ]},
      { h: "3. Educational Frameworks", p: [
        `Frameworks such as AuthorityOS are educational operating frameworks. They are not certifications, ` +
        `accreditations, compliance frameworks, legal standards, or regulatory standards.`,
      ]},
      { h: "4. User Responsibility", p: [
        `You are responsible for evaluating the suitability of any framework, template, or recommendation ` +
        `for your specific circumstances, and for the decisions and actions you take.`,
      ]},
      { h: "5. Contact", p: [CONTACT_BLOCK("Educational Use Disclaimer")] },
    ],
  },

  // ============================================================
  "affiliate-disclosure": {
    slug: "affiliate-disclosure",
    title: "Affiliate Disclosure",
    eyebrow: "Disclosure",
    intro:
      `${COMPANY} may participate in affiliate programs. This disclosure explains how that may affect ` +
      `content you encounter in the ecosystem.`,
    sections: [
      { h: "1. Affiliate Relationships", p: [
        `Some links on our websites and in our materials are affiliate links. If you click an affiliate ` +
        `link and complete a qualifying action (such as a purchase or sign-up), we may receive a ` +
        `commission at no additional cost to you.`,
      ]},
      { h: "2. Editorial Independence", p: [
        `Affiliate relationships do not influence editorial recommendations. We only feature tools, ` +
        `frameworks, or services that we believe may help our audience, and we strive to clearly indicate ` +
        `affiliate relationships where they exist.`,
      ]},
      { h: "3. No Endorsement Beyond Educational Use", p: [
        `Affiliate relationships do not constitute professional endorsement, certification, accreditation, ` +
        `or guarantee of any outcome from the affiliated product or service.`,
      ]},
      { h: "4. Your Choices", p: [
        `You are never required to use affiliate links. You may navigate directly to any product or ` +
        `service we discuss.`,
      ]},
      { h: "5. Contact", p: [CONTACT_BLOCK("Affiliate Disclosure")] },
    ],
  },

  // ============================================================
  "cookie-policy": {
    slug: "cookie-policy",
    title: "Cookie Policy",
    eyebrow: "Legal",
    intro:
      `This Cookie Policy explains how ${COMPANY} uses cookies and similar technologies.`,
    sections: [
      { h: "1. What Are Cookies", p: [
        `Cookies are small text files placed on your device by a website. We also use related technologies ` +
        `such as local storage and pixels.`,
      ]},
      { h: "2. Types of Cookies We Use", p: [
        `Essential — required for authentication, security, and core functionality.`,
        `Functional — remember preferences such as language or interface state.`,
        `Analytics — help us understand how the services are used so we can improve them.`,
        `Marketing — used (where applicable) to measure the effectiveness of campaigns.`,
      ]},
      { h: "3. Your Choices", p: [
        `You can control cookies through your browser settings and through any in-product consent controls. ` +
        `Disabling essential cookies may break certain features.`,
      ]},
      { h: "4. Third-Party Cookies", p: [
        `Service providers may set their own cookies subject to their own policies. We do not control ` +
        `third-party cookies.`,
      ]},
      { h: "5. Updates", p: [
        `We may update this Cookie Policy from time to time and will post the updated version with a new ` +
        `effective date.`,
      ]},
      { h: "6. Contact", p: [CONTACT_BLOCK("Cookie Policy")] },
    ],
  },

  // ============================================================
  "acceptable-use": {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    eyebrow: "Legal",
    intro:
      `This Acceptable Use Policy describes activities that are prohibited within the ${COMPANY} and ` +
      `AuthorityOS ecosystem.`,
    sections: [
      { h: "1. Prohibited Activities", p: [
        `Violating any law or regulation.`,
        `Infringing or misappropriating intellectual property rights.`,
        `Uploading malware, viruses, or harmful code.`,
        `Attempting to access systems, accounts, or data without authorization.`,
        `Interfering with the operation, security, or integrity of the services.`,
        `Scraping, harvesting, or bulk-downloading content other than as expressly permitted.`,
        `Reverse-engineering, disassembling, or attempting to derive source code, except where law expressly permits.`,
        `Misrepresenting AI-generated content as professional advice or as the work of a licensed professional.`,
        `Using AI assistants to provide regulated professional advice to third parties.`,
        `Generating content that is unlawful, defamatory, harassing, hateful, or sexually exploitative.`,
        `Circumventing usage limits, paywalls, or access controls.`,
      ]},
      { h: "2. Enforcement", p: [
        `We may investigate suspected violations and take any action we deem appropriate, including ` +
        `suspending or terminating accounts, removing content, and cooperating with law enforcement.`,
      ]},
      { h: "3. Reporting", p: [
        `Report suspected violations to ${SUPPORT_EMAIL}.`,
      ]},
      { h: "4. Contact", p: [CONTACT_BLOCK("Acceptable Use Policy")] },
    ],
  },

  // ============================================================
  "refund-policy": {
    slug: "refund-policy",
    title: "Refund Policy",
    eyebrow: "Legal",
    intro:
      `This Refund Policy describes the circumstances in which refunds may be available for purchases ` +
      `from ${COMPANY}.`,
    sections: [
      { h: "1. Digital Products & One-Time Services", p: [
        `Because digital products and one-time services (such as the Authority Audit) are delivered ` +
        `electronically or as personalized work product, all sales are generally final. We may, at our ` +
        `discretion, issue a refund within seven (7) days of purchase if the service has not yet been ` +
        `materially performed or delivered.`,
      ]},
      { h: "2. Subscriptions", p: [
        `Subscriptions may be cancelled at any time from your account or by contacting ${SUPPORT_EMAIL}. ` +
        `Cancellation stops future renewals. We do not generally refund the current billing period, ` +
        `though we will consider reasonable requests on a case-by-case basis.`,
      ]},
      { h: "3. How to Request a Refund", p: [
        `Email ${SUPPORT_EMAIL} with the subject line "Refund Request" and include your order details and ` +
        `the reason for the request. We aim to respond within five (5) business days.`,
      ]},
      { h: "4. Chargebacks", p: [
        `If you believe a charge is incorrect, please contact us first so we can resolve it. Initiating ` +
        `a chargeback without first contacting us may result in account suspension.`,
      ]},
      { h: "5. Contact", p: [CONTACT_BLOCK("Refund Policy")] },
    ],
  },

  // ============================================================
  "ip-policy": {
    slug: "ip-policy",
    title: "Intellectual Property Policy",
    eyebrow: "Legal",
    intro:
      `This Intellectual Property Policy describes ownership and permitted uses of materials within the ` +
      `${COMPANY} and AuthorityOS ecosystem.`,
    sections: [
      { h: "1. Our Materials", p: [
        `All frameworks (including AuthorityOS), templates, software, written content, designs, brand ` +
        `marks, logos, and AI-assistant configurations published by ${COMPANY} or its ecosystem properties ` +
        `(including MetabolicLabOS, BeaconsIEP, AASC, Build Ready, ChalkOS, and Kaiya Solutions) are owned ` +
        `by ${COMPANY} or our licensors and are protected by copyright, trademark, and other intellectual ` +
        `property laws.`,
      ]},
      { h: "2. Permitted Use", p: [
        `Subject to your compliance with our Terms, we grant you a limited, non-exclusive, non-transferable, ` +
        `revocable license to access and use our materials internally for your educational and business ` +
        `purposes. You may print or store copies of pages for personal reference.`,
      ]},
      { h: "3. Prohibited Use", p: [
        `You may not (a) resell, sublicense, or redistribute our materials; (b) remove or alter our notices ` +
        `or branding; (c) train machine-learning models on our materials without written permission; or ` +
        `(d) use our trademarks without express written consent.`,
      ]},
      { h: "4. Your Content", p: [
        `You retain ownership of content you submit. You grant us the limited license described in our Terms.`,
      ]},
      { h: "5. Trademark Notice", p: [
        `"The Authority Gap," "AuthorityOS," "MetabolicLabOS," "BeaconsIEP," "AASC," "Build Ready," ` +
        `"ChalkOS," and related marks are trademarks of ${COMPANY} or its affiliates.`,
      ]},
      { h: "6. Contact", p: [CONTACT_BLOCK("Intellectual Property Policy")] },
    ],
  },

  // ============================================================
  dmca: {
    slug: "dmca",
    title: "DMCA Policy",
    eyebrow: "Legal",
    intro:
      `${COMPANY} respects intellectual property rights and complies with the Digital Millennium Copyright ` +
      `Act (DMCA) and equivalent laws.`,
    sections: [
      { h: "1. Notice of Claimed Infringement", p: [
        `To submit a takedown notice, send the following in writing to ${SUPPORT_EMAIL} with the subject ` +
        `line "DMCA Notice":`,
        `(a) physical or electronic signature of the rights holder or authorized agent;`,
        `(b) identification of the copyrighted work claimed to be infringed;`,
        `(c) identification of the material claimed to be infringing, with enough detail for us to locate it;`,
        `(d) your contact information (name, address, telephone, email);`,
        `(e) a statement that you have a good-faith belief that the use is not authorized;`,
        `(f) a statement, under penalty of perjury, that the information is accurate and you are authorized to act.`,
      ]},
      { h: "2. Counter-Notice", p: [
        `If you believe content was removed in error, you may submit a counter-notice with the equivalent ` +
        `information and a consent to jurisdiction in ${GOVERNING_LAW}.`,
      ]},
      { h: "3. Repeat Infringers", p: [
        `We may terminate accounts of repeat infringers in appropriate circumstances.`,
      ]},
      { h: "4. Contact", p: [CONTACT_BLOCK("DMCA Notice")] },
    ],
  },

  // ============================================================
  accessibility: {
    slug: "accessibility",
    title: "Accessibility Statement",
    eyebrow: "Legal",
    intro:
      `${COMPANY} is committed to making the AuthorityOS ecosystem accessible to people with disabilities.`,
    sections: [
      { h: "1. Our Approach", p: [
        `We work toward conformance with the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA. ` +
        `We use semantic HTML, provide keyboard navigation where feasible, choose color contrast that ` +
        `supports readability, and continue to test and improve.`,
      ]},
      { h: "2. Ongoing Work", p: [
        `Accessibility is a continuous effort. We welcome reports of barriers so we can address them.`,
      ]},
      { h: "3. Feedback & Assistance", p: [
        `If you experience difficulty accessing any part of our services, email ${SUPPORT_EMAIL} with the ` +
        `subject line "Accessibility" and describe the issue. We aim to respond within five (5) business days.`,
      ]},
      { h: "4. Third-Party Content", p: [
        `Some content may be provided by third parties whose accessibility we do not control.`,
      ]},
      { h: "5. Contact", p: [CONTACT_BLOCK("Accessibility")] },
    ],
  },

  // ============================================================
  disclaimer: {
    slug: "disclaimer",
    title: "General Disclaimer",
    eyebrow: "Disclosure",
    intro:
      `The information provided by ${COMPANY} on ${DOMAIN} and across the AuthorityOS ecosystem is for ` +
      `general educational and informational purposes only.`,
    sections: [
      { h: "1. No Professional Advice", p: [
        `Nothing on this site or in our products constitutes legal, medical, mental health, therapy, tax, ` +
        `financial, investment, insurance, regulatory, accreditation, engineering, or accounting advice.`,
      ]},
      { h: "2. Educational Use Only", p: [
        `All frameworks, templates, AI assistants, assessments, scorecards, audits, and documentation ` +
        `systems are educational tools that may help, may support, or may improve processes. They are not ` +
        `guarantees of any outcome.`,
      ]},
      { h: "3. Independent Verification", p: [
        `Always independently verify information and consult appropriately qualified professionals before ` +
        `making decisions based on anything provided here.`,
      ]},
      { h: "4. Third-Party Links", p: [
        `Links to third-party sites are provided for convenience. We do not endorse and are not responsible ` +
        `for third-party content.`,
      ]},
      { h: "5. Limitation", p: [
        `Use of the services is at your own risk. See our Terms of Service for the full limitation of liability.`,
      ]},
      { h: "6. Contact", p: [CONTACT_BLOCK("General Disclaimer")] },
    ],
  },

  // ============================================================
  "authorityos-disclaimer": {
    slug: "authorityos-disclaimer",
    title: "AuthorityOS Framework Disclaimer",
    eyebrow: "Disclosure",
    intro:
      `AuthorityOS is the business operating framework published by ${COMPANY}. This disclaimer governs ` +
      `how AuthorityOS may and may not be characterized.`,
    sections: [
      { h: "1. What AuthorityOS Is", p: [
        `AuthorityOS is a business operating framework designed to help organizations improve visibility, ` +
        `documentation, authority, content systems, customer-acquisition systems, and operational workflows. ` +
        `It is an educational operating framework only.`,
      ]},
      { h: "2. What AuthorityOS Is Not", p: [
        `AuthorityOS is not a certification.`,
        `AuthorityOS is not an accreditation.`,
        `AuthorityOS is not a compliance framework.`,
        `AuthorityOS is not a legal standard.`,
        `AuthorityOS is not a regulatory standard.`,
        `AuthorityOS is not a guarantee of business success, revenue, traffic, or client acquisition.`,
      ]},
      { h: "3. No Guarantee Language", p: [
        `Statements about AuthorityOS are phrased in terms of what the framework "may help," "may support," ` +
        `"may assist," "may improve," "may contribute toward," "may provide guidance," or "may strengthen." ` +
        `Outcomes depend on factors outside the framework's control.`,
      ]},
      { h: "4. Ecosystem Properties", p: [
        `Properties built using the AuthorityOS framework — including MetabolicLabOS, BeaconsIEP, AASC, ` +
        `Build Ready, ChalkOS, and Kaiya Solutions — are laboratories demonstrating the framework in ` +
        `specific niches. Their existence does not represent or imply that any future deployment will ` +
        `replicate any specific outcome.`,
      ]},
      { h: "5. Independent Professional Advice", p: [
        `Implementing any AuthorityOS recommendation that touches legal, medical, financial, regulatory, ` +
        `or other professional domains should be reviewed by an appropriately qualified professional ` +
        `before action.`,
      ]},
      { h: "6. Contact", p: [CONTACT_BLOCK("AuthorityOS Framework Disclaimer")] },
    ],
  },
};

// Ordered groups for footer rendering
export const POLICY_GROUPS = [
  {
    label: "Legal",
    items: ["terms", "privacy", "cookie-policy", "acceptable-use", "ip-policy", "dmca", "accessibility", "refund-policy"],
  },
  {
    label: "Disclosure",
    items: ["disclaimer", "earnings-disclaimer", "educational-disclaimer", "affiliate-disclosure", "authorityos-disclaimer"],
  },
  {
    label: "AI Governance",
    items: ["ai-use", "ai-disclosure"],
  },
];

export const POLICY_LIST = Object.values(POLICIES);
